import React from "react"
import PropTypes from "prop-types"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import {
  EVENT_TYPE_LIST,
  EVENT_PLATFORM_LIST,
  TIMEZONE_LIST,
} from "constants/global"

const normalizeTimezone = (timezone) => {
  if (!timezone) return "America/Los_Angeles"
  const match = TIMEZONE_LIST.find(
    (tz) => tz.value === timezone || tz.text === timezone
  )
  return match ? match.value : timezone
}

const normalizeEventType = (eventType) => {
  if (!eventType) return "workshop"
  const match = EVENT_TYPE_LIST.find(
    (type) => type.value === eventType || type.text === eventType
  )
  return match ? match.value : eventType
}

export const buildEventFormValues = (afterJson = {}) => ({
  title: afterJson.title || "",
  shortDescription: afterJson.shortDescription || "",
  description: afterJson.description || "",
  eventType: normalizeEventType(afterJson.eventType ?? afterJson.type),
  platform: afterJson.platform || "zoom",
  location: afterJson.location || "",
  timezone: normalizeTimezone(
    afterJson.timezone ?? afterJson.timeZone ?? afterJson.eventTimezone
  ),
  startDate: afterJson.startDate || "",
  endDate: afterJson.endDate || "",
  registrationStartDate: afterJson.registrationStartDate || "",
  registrationEndDate: afterJson.registrationEndDate || "",
  tierProgramId: afterJson.tierProgramId ?? "",
  categoryId: afterJson.categoryId ?? "",
  maxParticipants: afterJson.maxParticipants ?? "",
  sortOrder: afterJson.sortOrder ?? 1,
  isFeatured: Boolean(afterJson.isFeatured),
  isActive: afterJson.isActive !== 0 && afterJson.isActive !== false,
  autoRegisterExternal: Boolean(afterJson.autoRegisterExternal),
  audienceEmailsEnabled: Boolean(afterJson.audienceEmailsEnabled),
  newMemberEmailsEnabled: Boolean(afterJson.newMemberEmailsEnabled),
  tags: Array.isArray(afterJson.tags) ? afterJson.tags.join(", ") : "",
  hubspotEventRegistrantId: afterJson.hubspotEventRegistrantId || "",
  hubspotEventProductId: afterJson.hubspotEventProductId || "",
  hubspotEventId: afterJson.hubspotEventId || "",
  aeventCalendarId: afterJson.aeventCalendarId || "",
  aeventWtlId: afterJson.aeventWtlId || "",
})

export const serializeEventFormValues = (formValues, originalAfterJson = {}) => {
  const tags = String(formValues.tags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean)

  return {
    ...originalAfterJson,
    title: formValues.title,
    shortDescription: formValues.shortDescription,
    description: formValues.description,
    eventType: formValues.eventType,
    platform: formValues.platform,
    location: formValues.location,
    timezone: formValues.timezone,
    startDate: formValues.startDate,
    endDate: formValues.endDate,
    registrationStartDate: formValues.registrationStartDate || undefined,
    registrationEndDate: formValues.registrationEndDate || undefined,
    tierProgramId: formValues.tierProgramId
      ? Number(formValues.tierProgramId)
      : undefined,
    categoryId: formValues.categoryId
      ? Number(formValues.categoryId)
      : undefined,
    maxParticipants: formValues.maxParticipants
      ? Number(formValues.maxParticipants)
      : undefined,
    sortOrder: Number(formValues.sortOrder) || 1,
    isFeatured: formValues.isFeatured ? 1 : 0,
    isActive: formValues.isActive ? 1 : 0,
    autoRegisterExternal: formValues.autoRegisterExternal ? 1 : 0,
    audienceEmailsEnabled: formValues.audienceEmailsEnabled ? 1 : 0,
    newMemberEmailsEnabled: formValues.newMemberEmailsEnabled ? 1 : 0,
    tags,
    hubspotEventRegistrantId: formValues.hubspotEventRegistrantId || undefined,
    hubspotEventProductId: formValues.hubspotEventProductId || undefined,
    hubspotEventId: formValues.hubspotEventId || undefined,
    aeventCalendarId: formValues.aeventCalendarId || undefined,
    aeventWtlId: formValues.aeventWtlId || undefined,
  }
}

const BatchEventEditForm = ({
  value,
  editable,
  tierPrograms,
  categories,
  onChange,
}) => {
  const updateField = (key, fieldValue) => {
    onChange({ ...value, [key]: fieldValue })
  }

  return (
    <div className="batch-event-form">
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Event Title"
            fullWidth
            value={value.title}
            disabled={!editable}
            onChange={(e) => updateField("title", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="batch-event-type-label">Event Type</InputLabel>
            <Select
              labelId="batch-event-type-label"
              label="Event Type"
              value={value.eventType}
              disabled={!editable}
              onChange={(e) => updateField("eventType", e.target.value)}
            >
              {EVENT_TYPE_LIST.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="batch-event-timezone-label">Event Timezone</InputLabel>
            <Select
              labelId="batch-event-timezone-label"
              label="Event Timezone"
              value={value.timezone}
              disabled={!editable}
              onChange={(e) => updateField("timezone", e.target.value)}
            >
              {TIMEZONE_LIST.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="batch-event-platform-label">Platform</InputLabel>
            <Select
              labelId="batch-event-platform-label"
              label="Platform"
              value={value.platform}
              disabled={!editable}
              onChange={(e) => updateField("platform", e.target.value)}
            >
              {EVENT_PLATFORM_LIST.map((platform) => (
                <MenuItem key={platform.value} value={platform.value}>
                  {platform.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Location"
            fullWidth
            value={value.location}
            disabled={!editable}
            onChange={(e) => updateField("location", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="batch-event-tier-label">Tier Program</InputLabel>
            <Select
              labelId="batch-event-tier-label"
              label="Tier Program"
              value={value.tierProgramId}
              disabled={!editable}
              onChange={(e) => updateField("tierProgramId", e.target.value)}
            >
              {tierPrograms.map((tier) => (
                <MenuItem key={tier.id} value={tier.id}>
                  {tier.type || tier.name || `Tier #${tier.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="batch-event-category-label">Category</InputLabel>
            <Select
              labelId="batch-event-category-label"
              label="Category"
              value={value.categoryId}
              disabled={!editable}
              onChange={(e) => updateField("categoryId", e.target.value)}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={value.startDate}
            disabled={!editable}
            onChange={(e) => updateField("startDate", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="End Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={value.endDate}
            disabled={!editable}
            onChange={(e) => updateField("endDate", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Short Description"
            fullWidth
            multiline
            minRows={2}
            value={value.shortDescription}
            disabled={!editable}
            onChange={(e) => updateField("shortDescription", e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.newMemberEmailsEnabled}
                disabled={!editable}
                onChange={(e) =>
                  updateField("newMemberEmailsEnabled", e.target.checked)
                }
              />
            }
            label="New Member Email Series Invite"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={value.description}
            disabled={!editable}
            onChange={(e) => updateField("description", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={value.isFeatured}
                disabled={!editable}
                onChange={(e) => updateField("isFeatured", e.target.checked)}
              />
            }
            label="Featured"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={value.isActive}
                disabled={!editable}
                onChange={(e) => updateField("isActive", e.target.checked)}
              />
            }
            label="Active"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={value.autoRegisterExternal}
                disabled={!editable}
                onChange={(e) =>
                  updateField("autoRegisterExternal", e.target.checked)
                }
              />
            }
            label="Auto Register External"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.audienceEmailsEnabled}
                disabled={!editable}
                onChange={(e) =>
                  updateField("audienceEmailsEnabled", e.target.checked)
                }
              />
            }
            label="Eligible Member Email Series"
          />
        </Grid>
      </Grid>
    </div>
  )
}

BatchEventEditForm.propTypes = {
  value: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  tierPrograms: PropTypes.array,
  categories: PropTypes.array,
  onChange: PropTypes.func.isRequired,
}

BatchEventEditForm.defaultProps = {
  editable: false,
  tierPrograms: [],
  categories: [],
}

export default BatchEventEditForm
